import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "./stores/auth-store";
import Layout from "./components/Layout";

// Eager load critical pages for faster initial render
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";

// Lazy load non-critical pages for code-splitting
const BookingPage = lazy(() => import("./pages/BookingPage"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const AdminPartnersPage = lazy(() => import("./pages/AdminPartnersPage"));
const AdminSchedulePage = lazy(() => import("./pages/AdminSchedulePage"));
const AdminTripsPage = lazy(() => import("./pages/AdminTripsPage"));
const AdminReportsPage = lazy(() => import("./pages/AdminReportsPage"));
const AdminEmailTemplatesPage = lazy(
  () => import("./pages/AdminEmailTemplatesPage"),
);
const AdminAuditLogsPage = lazy(() => import("./pages/AdminAuditLogsPage"));
const AdminShareLinksPage = lazy(() => import("./pages/AdminShareLinksPage"));
const AdminFortnoxPage = lazy(() => import("./pages/AdminFortnoxPage"));
const AdminArticlesPage = lazy(() => import("./pages/AdminArticlesPage"));
const AdminLocationsPage = lazy(() => import("./pages/AdminLocationsPage"));
const AdminImagesPage = lazy(() => import("./pages/AdminImagesPage"));
const LogbookPage = lazy(() => import("./pages/LogbookPage"));
const MaintenancePage = lazy(() => import("./pages/MaintenancePage"));
const AdminLogbookPage = lazy(() => import("./pages/AdminLogbookPage"));
const AdminSystemLogbookPage = lazy(() => import("./pages/AdminSystemLogbookPage"));
const LogbookRulesPage = lazy(() => import("./pages/LogbookRulesPage"));
const LogbookMapPage = lazy(() => import("./pages/LogbookMapPage"));
const LogbookCalendarPage = lazy(() => import("./pages/LogbookCalendarPage"));
const AgencyPage = lazy(() => import("./pages/AgencyPage"));
const AdminCompanyPage = lazy(() => import("./pages/AdminCompanyPage"));
const AdminContactPage = lazy(() => import("./pages/AdminContactPage"));
const TripsPage = lazy(() => import("./pages/TripsPage"));
const TripDetailPage = lazy(() => import("./pages/TripDetailPage"));
const BookingPrintPage = lazy(() => import("./pages/BookingPrintPage"));
import AdminShell from "./components/AdminShell";
import SettingsPage from "./pages/SettingsPage";

const PublicCalendarPage = lazy(() => import("./pages/PublicCalendarPage"));
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const PaymentReturnPage = lazy(() => import("./pages/PaymentReturnPage"));

/**
 * Loading fallback component
 */
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
}

/**
 * Protected route wrapper
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Admin-only route wrapper
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

/**
 * Crew-or-admin route wrapper (the logbook)
 */
function CrewRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== "admin" && user?.role !== "crew") {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

/** Crew land on the logbook; everyone else on the dashboard. */
function HomeRedirect() {
  const role = useAuthStore((state) => state.user?.role);
  return <Navigate to={role === "crew" ? "/logbook" : "/dashboard"} replace />;
}

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomeRedirect />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route
          path="book"
          element={
            <Suspense fallback={<PageLoader />}>
              <BookingPage />
            </Suspense>
          }
        />
        <Route
          path="trips"
          element={
            <Suspense fallback={<PageLoader />}>
              <TripsPage />
            </Suspense>
          }
        />
        <Route
          path="trips/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <TripDetailPage />
            </Suspense>
          }
        />
        <Route
          path="bookings/:id/print"
          element={
            <Suspense fallback={<PageLoader />}>
              <BookingPrintPage />
            </Suspense>
          }
        />
        <Route
          path="agency"
          element={
            <Suspense fallback={<PageLoader />}>
              <AgencyPage />
            </Suspense>
          }
        />
        <Route
          path="calendar"
          element={
            <Suspense fallback={<PageLoader />}>
              <CalendarPage />
            </Suspense>
          }
        />
        <Route
          path="help"
          element={
            <Suspense fallback={<PageLoader />}>
              <HelpPage />
            </Suspense>
          }
        />

        <Route
          path="logbook"
          element={
            <CrewRoute>
              <Suspense fallback={<PageLoader />}>
                <LogbookPage />
              </Suspense>
            </CrewRoute>
          }
        />
        <Route
          path="maintenance"
          element={<CrewRoute><Suspense fallback={<PageLoader />}><MaintenancePage /></Suspense></CrewRoute>}
        />
        <Route
          path="logbook/calendar"
          element={
            <CrewRoute>
              <Suspense fallback={<PageLoader />}>
                <LogbookCalendarPage />
              </Suspense>
            </CrewRoute>
          }
        />
        <Route
          path="logbook/map"
          element={
            <CrewRoute>
              <Suspense fallback={<PageLoader />}>
                <LogbookMapPage />
              </Suspense>
            </CrewRoute>
          }
        />
        <Route
          path="logbook/rules"
          element={
            <CrewRoute>
              <Suspense fallback={<PageLoader />}>
                <LogbookRulesPage />
              </Suspense>
            </CrewRoute>
          }
        />
        {/* Settings: every admin page sits in the shell with the side menu */}
        <Route element={<AdminShell />}>
          <Route
            path="settings"
            element={
              <AdminRoute>
                <SettingsPage />
              </AdminRoute>
            }
          />
        <Route
          path="admin/logbook"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminLogbookPage />
              </Suspense>
            </AdminRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="admin/partners"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminPartnersPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/schedule"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminSchedulePage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/trips"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminTripsPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/reports"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminReportsPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/email-templates"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminEmailTemplatesPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/system/logbook"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminSystemLogbookPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/audit-logs"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminAuditLogsPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/share-links"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminShareLinksPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/fortnox"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminFortnoxPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/articles"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminArticlesPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/locations"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminLocationsPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/company"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminCompanyPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/images"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminImagesPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="admin/contact"
          element={
            <AdminRoute>
              <Suspense fallback={<PageLoader />}>
                <AdminContactPage />
              </Suspense>
            </AdminRoute>
          }
        />
        </Route>
      </Route>

      {/* Public calendar view (anonymous access via share link) */}
      <Route
        path="/shared/:token"
        element={
          <Suspense fallback={<PageLoader />}>
            <PublicCalendarPage />
          </Suspense>
        }
      />

      {/* Public payment pages (anonymous access via email link) */}
      <Route
        path="/pay/return"
        element={
          <Suspense fallback={<PageLoader />}>
            <PaymentReturnPage />
          </Suspense>
        }
      />
      <Route
        path="/pay/:confirmationCode"
        element={
          <Suspense fallback={<PageLoader />}>
            <PaymentPage />
          </Suspense>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
